import React, { useState, useRef, useEffect } from "react";
import Grid from "@mui/material/Grid2";
import { Button, Typography, MenuItem } from "@mui/material";
import MaterialTable from "material-table";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { withStyles } from "@mui/styles";
import { Styles } from "../../../../config/design/styles";
import { defaultVariables } from "../../../../config";
import { TableCount, TablePage } from "../../../../config/Permissions";
import { getDataTableLocalization } from "../../../../config/Translate";
import { tableIcons } from "../../../../config/design/TableIcons";
import { showTableDate } from "../../../../config/values";
import AppAnimate from "../../../components/AppAnimate";
import Title from "../../../components/elements/Title";
import RayaField from "../../../components/elements/fields/RayaField";
import VendorLeadDetailDialog from "./VendorLeadDetailDialog";
import { ChevronRightRounded } from "@mui/icons-material";

function VendorLeadsPage({ classes }) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const tableRef = useRef();
    const [selectedLead, setSelectedLead] = useState(null);
    const [statusFilter, setStatusFilter] = useState("all");
    const [repFilter, setRepFilter] = useState("");
    const [reps, setReps] = useState([]);

    const loginToken = localStorage.getItem("ACT");
    const config = {
        headers: {
            Accept: "application/json",
            Authorization: `Bearer ${loginToken}`,
        },
    };

    useEffect(() => {
        axios
            .get(`${defaultVariables.url}/marketing-representatives/quickView?per_page=500`, config)
            .then((response) => setReps(response.data?.data || []))
            .catch(() => {});
    }, []);

    const reload = () => {
        tableRef.current && tableRef.current.onQueryChange();
    };

    const columns = [
        {
            title: t("registerDate"),
            field: "created_at",
            editable: "never",
            render: (rowData) => (
                <Typography className={classes.dateTable}>{showTableDate(rowData.created_at)}</Typography>
            ),
        },
        {
            title: t("marketingRepName"),
            field: "rep_name",
            editable: "never",
        },
        {
            title: t("marketingShopName"),
            field: "shop_name",
            editable: "never",
        },
        {
            title: t("mobile"),
            field: "mobile",
            editable: "never",
        },
        {
            title: t("marketingRegion"),
            field: "region",
            editable: "never",
        },
        {
            title: t("status"),
            field: "status",
            editable: "never",
            render: (rowData) => (
                <Typography
                    className={`table-badge ${
                        rowData.status === "approved" ? "active" : rowData.status === "pending" ? "pending" : "inactive"
                    }`}
                >
                    {t(`marketingLeadStatus_${rowData.status}`)}
                </Typography>
            ),
        },
        {
            title: t("activity"),
            field: "activity",
            editable: "never",
            render: (rowData) => (
                <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    endIcon={<ChevronRightRounded />}
                    onClick={() => setSelectedLead(rowData.id)}
                >
                    {t("details")}
                </Button>
            ),
        },
    ];

    return (
        <>
            <AppAnimate animation="transition.slideUpIn" delay={200} title={t("marketingLeadsTitle")}>
                <Grid container spacing={2}>
                    <Grid item size={12}>
                        <Title align="left" color="text" className="panel-title">
                            {t("marketingLeadsTitle")}
                        </Title>
                    </Grid>
                    <Grid item size={{ xs: 12, md: 4 }}>
                        <RayaField
                            select
                            fullWidth
                            variant="outlined"
                            label={t("status")}
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setTimeout(reload, 0);
                            }}
                        >
                            <MenuItem value="all">{t("all")}</MenuItem>
                            <MenuItem value="pending">{t("marketingLeadStatus_pending")}</MenuItem>
                            <MenuItem value="approved">{t("marketingLeadStatus_approved")}</MenuItem>
                        </RayaField>
                    </Grid>
                    <Grid item size={{ xs: 12, md: 4 }}>
                        <RayaField
                            select
                            fullWidth
                            variant="outlined"
                            label={t("marketingRepName")}
                            value={repFilter}
                            onChange={(e) => {
                                setRepFilter(e.target.value);
                                setTimeout(reload, 0);
                            }}
                        >
                            <MenuItem value="">{t("all")}</MenuItem>
                            {reps.map((rep) => (
                                <MenuItem key={rep.id} value={String(rep.id)}>
                                    {rep.first_name} {rep.last_name}
                                </MenuItem>
                            ))}
                        </RayaField>
                    </Grid>
                    <Grid item size={12} className="main-table">
                        <MaterialTable
                            tableRef={tableRef}
                            icons={tableIcons}
                            title=" "
                            localization={getDataTableLocalization()}
                            columns={columns}
                            data={(query) =>
                                new Promise((resolve) => {
                                    let url = `${defaultVariables.url}/vendor-leads/quickView?page=${query.page + 1}&per_page=${query.pageSize}&search=${query.search || ""}`;
                                    if (statusFilter !== "all") url += `&status=${statusFilter}`;
                                    if (repFilter) url += `&marketing_representative_id=${repFilter}`;

                                    axios
                                        .get(url, config)
                                        .then((response) => {
                                            const rows = response.data?.data || [];
                                            resolve({
                                                data: rows.map((row) => ({
                                                    ...row,
                                                    rep_name: row.marketing_representative
                                                        ? `${row.marketing_representative.first_name} ${row.marketing_representative.last_name}`
                                                        : "-",
                                                    region: row.marketing_representative?.region || "-",
                                                })),
                                                page: (response.data?.current_page || 1) - 1,
                                                totalCount: response.data?.total || 0,
                                            });
                                        })
                                        .catch(() => resolve({ data: [], page: 0, totalCount: 0 }));
                                })
                            }
                            options={{
                                pageSize: TablePage,
                                pageSizeOptions: TableCount,
                                sorting: false,
                                draggable: false,
                            }}
                        />
                    </Grid>
                </Grid>
            </AppAnimate>

            {!!selectedLead && (
                <VendorLeadDetailDialog
                    leadId={selectedLead}
                    onClose={() => setSelectedLead(null)}
                    onUpdated={reload}
                    onApproved={() => {
                        setSelectedLead(null);
                        reload();
                        enqueueSnackbar(t("marketingApproveSuccess"), { variant: "success" });
                    }}
                />
            )}
        </>
    );
}

export default withStyles(Styles)(VendorLeadsPage);
